"use client";

import { useReadContract } from "wagmi";
import ContractAddress from "@/contract/addresses";
import { IS_GATEWAY_V2 } from "@/contract/gateway-version";

/**
 * The share of every purchase's gross BV that is paid out as a referral, in
 * wei (0.09 ether = 9%).
 *
 * WHY THE CLIENT HAS TO DO THIS
 * The gateway credits genealogy legs with EFFECTIVE bv:
 *
 *   referralCut  = grossBv * referralPercent / 1 ether
 *   effectiveBv  = grossBv - referralCut
 *
 * The backend stores only the gross cart BV — there is no gross/net pair in
 * `order_items`, and computing the cut would need this value, which the backend
 * cannot read because it makes no RPC calls. So anything mixing on-chain BV
 * (net) with pending BV (gross) has to apply the cut here.
 *
 * Read live rather than hardcoded: `setReferralPercent` is `onlyDao`, so 9% can
 * change without a frontend deploy.
 *
 * Fails OPEN (0 = no cut) when the read is unavailable, which is also the
 * correct answer on V1 — that generation has no referral cut and no such
 * function, so a pending order there really is worth its gross value.
 */

const REFERRAL_PERCENT_ABI = [
  {
    type: "function",
    name: "referralPercent",
    stateMutability: "view",
    inputs: [],
    outputs: [{ name: "", type: "uint256", internalType: "uint256" }],
  },
] as const;

export const useReferralPercent = (): bigint => {
  const { data } = useReadContract({
    address: ContractAddress.GATEWAY,
    abi: REFERRAL_PERCENT_ABI,
    functionName: "referralPercent",
    // V2-only; reading it on V1 reverts.
    query: { enabled: IS_GATEWAY_V2 },
  });

  return typeof data === "bigint" ? data : 0n;
};

/**
 * Applies the referral cut to a gross BV figure, mirroring the gateway's own
 * arithmetic so a pending order reads the same as it will once the chain counts
 * it. Returns the input unchanged when the percent is unknown or zero.
 */
export const toEffectiveBv = (
  grossBv: number,
  referralPercentWei: bigint,
): number => {
  if (referralPercentWei <= 0n || !Number.isFinite(grossBv) || grossBv <= 0) {
    return grossBv;
  }

  const percent = Number(referralPercentWei) / 1e18;
  return grossBv * (1 - percent);
};
